import { AddCircleOutlined, DeleteOutlineOutlined, FilterAltOffOutlined, WorkOffOutlined, WorkOutlineOutlined } from "@mui/icons-material";
import PersonOutlineOutlined from "@mui/icons-material/PersonOutlineOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { alpha, Box, IconButton, type Theme, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { type Dispatch, type FC, type ReactNode, type SetStateAction, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { ChipListCell } from "../../../../components/UIToolkit/Cells/ChipListCell";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { formatHourRange, type HourRange } from "../../../../components/UIToolkit/Inputs/TimeRangeListField";
import { ColorDot } from "../../../../components/UIToolkit/molecules/ColorDot";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import { type StatusChecklistItem, SystemStatusChecklist } from "../../../../components/UIToolkit/molecules/SystemStatusChecklist";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { type RowGroupingOptions, SystemDataGrid, type SystemGridColDef } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useAllJobs } from "../../../../hooks/useJobs";
import { useRunWageBreakdown } from "../../../../hooks/useRunWageBreakdown";
import { backendService } from "../../../../services/backend.service";
import { ALPHA, CONTENT_WIDTH, FONT_SIZE, INK, NEUTRAL } from "../../../../theme/tokens";
import type { DepartmentJobPpdOverride, Job } from "../../../../utils/models";
import { useJobNotEnabledEmployees } from "./hrisMapping/hrisMappingHooks";
import { HoverActionCell } from "./hrisMapping/utils";
import { isUnknownJobId, type JobGroup, OTHER_GROUP, useJobGroups } from "./jobGroups";
import { jobPpdOverridesQueryKey, prunePendingJobPpd, resolveJobPpd, setPendingJobPpd } from "./jobPpdOverrides";
import { ManageJobsDialog } from "./ManageJobsDialog";
import { ShiftTimesEditor, shiftTimesEditorHeight } from "./ShiftTimesEditor";
import { ShiftTimesFallbackCard } from "./ShiftTimesFallbackCard";
import { collectPartialHourOptions } from "./shiftTimes";
import type { GeneralFormData } from "./types";

/** "no filter" sentinel for the two toolbar selects — an empty value keeps every row. */
const ANY_FILTER = "";
const PPD_INCLUDED = "included";
const PPD_EXCLUDED = "excluded";

/**
 * Trailing band for jobs that still carry configured shift times but are no longer enabled for the
 * department. They are not department jobs, so they have no PPD inclusion, no override and no
 * removal — only their times, read-only, plus the warning that the next save drops them. Prefixed so
 * it can never collide with a `Position` group key.
 */
const ORPHAN_GROUP = "__not_enabled__";

/** One row of the grid: an enabled job, or a no-longer-enabled job that still has times configured. */
type JobRow = {
	id: string;
	/** `job.name` — the stable slug the per-row test ids are keyed on. */
	slug: string;
	displayName: string;
	groupKey: string;
	/** Carried on the row (not just the band) so the search box can match a group name too. */
	groupLabel: string;
	/** PPD inclusion after the pending edit and the department override are applied over the job default. */
	excluded: boolean;
	/** The job's own catalog default, i.e. what "reset" returns this row to. */
	defaultExcluded: boolean;
	/** What the server currently holds for this job, or `undefined` for no override row. */
	persistedExcluded: boolean | undefined;
	isOverridden: boolean;
	partialBlockShifts: number[];
	fullShiftTimes: HourRange[];
	/** Configured shift times for a job the department no longer enables — read-only, dropped on save. */
	isOrphan: boolean;
};

const PPD_COLUMN_WIDTH = 190;
const OVERRIDE_COLUMN_WIDTH = 110;
const ACTIONS_COLUMN_WIDTH = 56;
/** Three full ranges read better than five stubs; the rest collapse into "+N" (the panel has them all). */
const MAX_VISIBLE_SHIFT_TIME_CHIPS = 3;
const MAX_VISIBLE_PARTIAL_CHIPS = 4;
/** Brand rule down the open panel's leading edge, tying it to the row above. */
const EXPANDED_PANEL_RULE_PX = 2;
/** Names printed per affected job before the rest collapse into "+N more" — enough to recognise who. */
const MAX_VISIBLE_AFFECTED_NAMES = 5;

/**
 * The department's Jobs surface, and the single place per-job shift data is read and written. One
 * grid replaces the old multi-select + PPD table AND the separate Shift Lengths table, where "Full
 * shift times" used to appear twice — read-only here, editable there (DAT-804).
 *
 * Shift times open in a panel under the row rather than inline: both controls are multi-value, and
 * 40+ rows each carrying two of them stops the grid being scannable.
 *
 * Everything here is form state behind the tab's one Save: job enable/remove, shift times, and PPD
 * inclusion. PPD overrides live in a table of their own, so they are flushed by their own endpoint
 * right after the config PUT succeeds (`useFlushJobPpdOverrides`) — the split is an implementation
 * detail of the API, never of the save model.
 */
export const DepartmentJobsConfig: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isNewDept: boolean;
	/**
	 * Job mode (the feature flag, or any new department). Per-job shift times only exist in job mode;
	 * in role mode the column shows an em-dash rather than projecting role data onto a job row.
	 * Resolved by the tab and passed down so the flag is read once.
	 */
	jobMode: boolean;
}> = ({ formData, setFormData, isNewDept, jobMode }) => {
	const { t } = useTranslation();
	const { chosenDepartment, refreshDepartmentData } = useHospitalData();
	const { track } = useAnalytics();
	const { data: allJobs = [], isLoading: isLoadingJobs } = useAllJobs();
	const { groups, groupKeyByJobId, groupByKey, groupOrder, isLoading: isLoadingGroups } = useJobGroups(allJobs);

	const departmentId = chosenDepartment.department_id;
	const groupFilterId = useId();
	const ppdFilterId = useId();
	const [manageOpen, setManageOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [groupFilter, setGroupFilter] = useState<string>(ANY_FILTER);
	const [ppdFilter, setPpdFilter] = useState<string>(ANY_FILTER);
	// ── PPD overrides (persisted set; edits are buffered in `formData.jobPpdOverrides`) ───────────
	const { data: overrides = [] } = useQuery({
		queryKey: jobPpdOverridesQueryKey(departmentId),
		queryFn: async () => {
			const res = await backendService.getDepartmentJobPpdOverrides(departmentId);
			return res.data;
		},
		enabled: !!departmentId && !isNewDept,
	});

	const overrideMap = useMemo(() => {
		const map = new Map<string, DepartmentJobPpdOverride>();
		for (const override of overrides) map.set(override.jobId, override);
		return map;
	}, [overrides]);

	// ── Employees holding a job this department doesn't enable (DAT-834) ──────────────────────────
	// Informational only. This replaced the Team page's `job_not_enabled` decision, which asked an
	// admin to deactivate somebody over a settings mismatch; the fix is here, one button away.
	// Server truth (who is active, what their primary job is), so it is deliberately NOT derived from
	// `formData.jobIds` — an unsaved edit must not make the warning appear or vanish.
	const { data: jobNotEnabledEmployees = [] } = useJobNotEnabledEmployees(departmentId, !isNewDept);

	/** Affected employees folded onto the job that needs enabling — the unit the fix is applied in. */
	const affectedJobs = useMemo(() => {
		const byJob = new Map<string, { jobId: string; jobName: string | null; total: number; names: string[] }>();
		for (const employee of jobNotEnabledEmployees) {
			const group = byJob.get(employee.jobId) ?? { jobId: employee.jobId, jobName: employee.jobName, total: 0, names: [] };
			group.total += 1;
			// A record carrying neither first nor last name sends `null` rather than `""`, so it is counted
			// and left out of the name line instead of printing a gap between two separators.
			if (employee.name) group.names.push(employee.name);
			byJob.set(employee.jobId, group);
		}
		// Most people first: that is the job worth enabling first.
		return Array.from(byJob.values()).sort((a, b) => b.total - a.total);
	}, [jobNotEnabledEmployees]);

	const affectedJobItems = useMemo<StatusChecklistItem[]>(
		() =>
			affectedJobs.map((group) => {
				const shown = group.names.slice(0, MAX_VISIBLE_AFFECTED_NAMES);
				const hidden = group.total - shown.length;
				const nameLine = [shown.join(" · "), hidden > 0 ? t("department.config.jobs.notEnabled.moreEmployees", { count: hidden }) : null]
					.filter(Boolean)
					.join(" · ");
				// The no-job sentinel is a row every picker hides: naming it shows the reader a job they
				// have never seen, and "add the job" is advice that leads nowhere, since it is not a job.
				// Its own label and its own remedy — set the person's job on their profile.
				const isNoJob = isUnknownJobId(group.jobId, allJobs);
				return {
					id: group.jobId,
					// A job id with no `job` row left behind gets the same localized label the grid uses for
					// one, never a raw UUID.
					label: isNoJob ? t("department.config.jobs.notEnabled.noJob.label") : (group.jobName ?? t("department.config.shifts.job.unknown")),
					description: isNoJob ? (
						<>
							{nameLine}
							<Box component="span" sx={{ display: "block" }}>
								{t("department.config.jobs.notEnabled.noJob.fix")}
							</Box>
						</>
					) : (
						nameLine || undefined
					),
					state: "blocked" as const,
					statusLabel: t("department.config.jobs.notEnabled.employeeCount", { count: group.total }),
					icon: isNoJob ? <PersonOutlineOutlined /> : <WorkOffOutlined />,
					dataTestId: `dept-jobs-not-enabled-job-${group.jobId}`,
				};
			}),
		[affectedJobs, allJobs, t],
	);

	// One view event per department per distinct count, so a re-render or a tab switch doesn't inflate it.
	const trackedNotEnabledView = useRef<string | null>(null);
	useEffect(() => {
		if (jobNotEnabledEmployees.length === 0) return;
		const signature = `${departmentId}:${jobNotEnabledEmployees.length}`;
		if (trackedNotEnabledView.current === signature) return;
		trackedNotEnabledView.current = signature;
		track("dept_jobs_not_enabled_banner_view", { department_id: departmentId, count: jobNotEnabledEmployees.length, job_count: affectedJobs.length });
	}, [departmentId, jobNotEnabledEmployees.length, affectedJobs.length, track]);

	/** Buffers one row's PPD inclusion. No request: the page's Save flushes the whole map. */
	const applyPpd = useCallback(
		(row: JobRow, nextExcluded: boolean) => {
			setFormData((prev) => ({
				...prev,
				jobPpdOverrides: setPendingJobPpd(prev.jobPpdOverrides, row.id, nextExcluded, row.defaultExcluded, row.persistedExcluded),
			}));
		},
		[setFormData],
	);

	/** The reset-to-default affordance — the same write as toggling the row back to its catalog value. */
	const resetPpd = useCallback((row: JobRow) => applyPpd(row, row.defaultExcluded), [applyPpd]);

	// ── Rows ──────────────────────────────────────────────────────────────────────────────────────
	const rows = useMemo<JobRow[]>(() => {
		const jobsById = new Map(allJobs.map((job) => [job.id, job]));
		const enabled = formData.jobIds
			.map((jobId) => jobsById.get(jobId))
			.filter((job): job is Job => !!job && job.name !== "unknown")
			.map((job) => {
				const persistedExcluded = overrideMap.get(job.id)?.excludeFromPpd;
				const groupKey = groupKeyByJobId.get(job.id) ?? OTHER_GROUP;
				return {
					id: job.id,
					slug: job.name,
					displayName: job.displayName,
					groupKey,
					groupLabel: groupByKey.get(groupKey)?.label ?? t("department.config.jobs.ungrouped"),
					...resolveJobPpd(formData.jobPpdOverrides, job.id, persistedExcluded, job.excludeFromPpd),
					defaultExcluded: job.excludeFromPpd,
					persistedExcluded,
					// Per-job shift times are a job-mode concept; role mode has no per-job answer to show.
					partialBlockShifts: jobMode ? (formData.jobShiftTimes[job.id]?.partialBlockShifts ?? []) : [],
					fullShiftTimes: jobMode ? (formData.jobShiftTimes[job.id]?.fullShiftTimes ?? []) : [],
					isOrphan: false,
				};
			})
			.sort((a, b) => a.displayName.localeCompare(b.displayName));

		if (!jobMode) return enabled;

		// Shift times configured for a job that is no longer enabled — reachable the moment a row with
		// times is removed, since removal is buffered. They kept their own read-only row in the table
		// this grid absorbed; dropping them here would hide, in silence, data the next save discards.
		const enabledIds = new Set(formData.jobIds);
		const orphans = Object.keys(formData.jobShiftTimes)
			.filter((jobId) => !enabledIds.has(jobId))
			.map((jobId) => {
				const job = jobsById.get(jobId);
				return {
					id: jobId,
					slug: job?.name ?? jobId,
					// A job id with no entry in `allJobs` shows a localized label, never the raw UUID — this
					// is reachable while `useAllJobs` is still loading.
					displayName: job?.displayName ?? t("department.config.shifts.job.unknown"),
					groupKey: ORPHAN_GROUP,
					groupLabel: t("department.config.jobs.group.notEnabled"),
					excluded: false,
					defaultExcluded: false,
					persistedExcluded: undefined,
					isOverridden: false,
					partialBlockShifts: formData.jobShiftTimes[jobId]?.partialBlockShifts ?? [],
					fullShiftTimes: formData.jobShiftTimes[jobId]?.fullShiftTimes ?? [],
					isOrphan: true,
				};
			})
			.sort((a, b) => a.displayName.localeCompare(b.displayName));

		return [...enabled, ...orphans];
	}, [allJobs, formData.jobIds, formData.jobShiftTimes, formData.jobPpdOverrides, overrideMap, groupKeyByJobId, groupByKey, jobMode, t]);

	/** Pick-list of partial lengths already in use anywhere in the department, offered on every row. */
	const hourOptions = useMemo(
		() => collectPartialHourOptions(formData.jobShiftTimes, formData.partialBlockShifts),
		[formData.jobShiftTimes, formData.partialBlockShifts],
	);

	// ── Shift times (buffered in formData, edited in the row's panel) ─────────────────────────────
	/**
	 * Which rows have their editor panel open. The grid owns the expansion itself; this mirror exists
	 * only so the resting cells can mark themselves open (`ChipListCell`'s `isOpen`) — without it, an
	 * open panel has no visible link to the cells that opened it. A set, not one id: the grid keeps
	 * every row the user opened open.
	 */
	const [openEditorRowIds, setOpenEditorRowIds] = useState<ReadonlySet<string>>(() => new Set());
	const handleExpandedRowsChange = useCallback((ids: (string | number)[]) => setOpenEditorRowIds(new Set(ids.map(String))), []);

	const getCellExpandContent = useCallback(
		(row: JobRow, field: keyof JobRow): ReactNode => {
			// Role mode keeps shift times on the ROLE — that table is still the editor there, and a job
			// row has nothing to open. Only the two shift columns are expandable.
			if (!jobMode || (field !== "fullShiftTimes" && field !== "partialBlockShifts")) return null;
			return (
				<ShiftTimesEditor
					scope="job"
					rowKey={row.id}
					hours={row.partialBlockShifts}
					fullShiftTimes={row.fullShiftTimes}
					hourOptions={hourOptions}
					setFormData={setFormData}
					// The save path filters no-longer-enabled jobs out (and the backend rejects them), so
					// leaving them editable would discard the edit in silence.
					bounded
					readOnly={row.isOrphan}
					hint={row.isOrphan ? t("department.config.shifts.jobNotEnabled") : undefined}
					dataTestId={`dept-job-shift-times-editor-${row.slug}`}
				/>
			);
		},
		[jobMode, hourOptions, setFormData, t],
	);

	const getCellExpandContentHeight = useCallback((row: JobRow): number => shiftTimesEditorHeight(row.isOrphan), []);

	/**
	 * The open panel reads as an inset of the row above it, not as another row: a faint brand wash and a
	 * brand rule down its leading edge. Without it the editor floats in the table body with nothing
	 * tying it to the job it belongs to.
	 */
	const expandedContentSx = useMemo(
		() => (theme: Theme) => ({
			// Fills the detail ROW, not just its content: the cell is content-sized by default, so the wash
			// and the rule stopped short and left a white strip under the editor.
			height: "100%",
			backgroundColor: alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY),
			borderInlineStart: `${EXPANDED_PANEL_RULE_PX}px solid ${theme.palette.primary.main}`,
		}),
		[],
	);

	/** Groups present in the CURRENT row set, so the band order and the filter list can't offer an empty group. */
	const presentGroups = useMemo<JobGroup[]>(() => {
		const present = new Set(rows.map((row) => row.groupKey));
		return groups.filter((group) => present.has(group.key));
	}, [rows, groups]);

	/**
	 * The group filter actually applied. It can differ from `groupFilter`: `presentGroups` is recomputed
	 * from the current rows, so removing the last row of the filtered group drops that group's option —
	 * and `SystemDropdown` with no matching item falls back to its placeholder, which reads "All groups".
	 * Left unreconciled, the control claimed no filter while every remaining row was still being dropped.
	 * Derived rather than reset in an effect, so there is no render where the two disagree; the user's
	 * `groupFilter` is left alone, so a group that comes back (an undone removal) filters again.
	 */
	const effectiveGroupFilter = useMemo(
		() => (groupFilter !== ANY_FILTER && !presentGroups.some((group) => group.key === groupFilter) ? ANY_FILTER : groupFilter),
		[groupFilter, presentGroups],
	);

	/**
	 * Everything the two filters and the search kept. The search box is CONTROLLED (`searchValue` +
	 * `onSearchChange`) so the two toolbar filters and the search compose in one predicate this
	 * component owns; the grid re-applies the same predicate over these rows, which is a no-op.
	 */
	const filteredRows = useMemo(() => {
		const query = search.trim().toLowerCase();
		return rows.filter((row) => {
			if (effectiveGroupFilter !== ANY_FILTER && row.groupKey !== effectiveGroupFilter) return false;
			// A no-longer-enabled job has no PPD inclusion at all, so it answers neither side of that filter.
			if (ppdFilter !== ANY_FILTER && row.isOrphan) return false;
			if (ppdFilter === PPD_INCLUDED && row.excluded) return false;
			if (ppdFilter === PPD_EXCLUDED && !row.excluded) return false;
			if (query && !row.displayName.toLowerCase().includes(query) && !row.groupLabel.toLowerCase().includes(query)) return false;
			return true;
		});
	}, [rows, effectiveGroupFilter, ppdFilter, search]);

	// ── Removing a job ────────────────────────────────────────────────────────────────────────────
	// Buffered: the id leaves `formData.jobIds` and the tab's Save applies it, so the backend's
	// in-use conflict guard (409 → `jobRemovalConflicts`) still runs exactly as before.
	const removeJob = useCallback(
		(row: JobRow) => {
			track("dept_job_remove", { department_id: departmentId, job_id: row.id });
			setFormData((prev) => {
				const jobIds = prev.jobIds.filter((jobId) => jobId !== row.id);
				return { ...prev, jobIds, jobPpdOverrides: prunePendingJobPpd(prev.jobPpdOverrides, jobIds) };
			});
		},
		[setFormData, track, departmentId],
	);

	const handleJobCreated = useCallback(
		(job: Job) => {
			setFormData((prev) => (prev.jobIds.includes(job.id) ? prev : { ...prev, jobIds: [...prev.jobIds, job.id] }));
			void refreshDepartmentData(departmentId, false);
		},
		[setFormData, refreshDepartmentData, departmentId],
	);

	// ── Columns ───────────────────────────────────────────────────────────────────────────────────
	const columns = useMemo<SystemGridColDef<JobRow>[]>(() => {
		const jobColumn: SystemGridColDef<JobRow> = {
			field: "displayName",
			headerName: t("job"),
			flex: 1.4,
			minWidth: 180,
			renderCell: (params) => <Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS }}>{params.row.displayName}</Typography>,
		};

		const ppdColumn: SystemGridColDef<JobRow> = {
			field: "excluded",
			headerName: t("department.config.jobs.col.ppdInclusion"),
			width: PPD_COLUMN_WIDTH,
			hideHeaderSeparator: true,
			renderCell: (params) =>
				params.row.isOrphan ? null : (
					<SystemSwitch
						checked={!params.row.excluded}
						onChange={(event) => {
							const nextExcluded = !event.target.checked;
							track("dept_job_ppd_override_toggle", { department_id: departmentId, job_id: params.row.id, exclude_from_ppd: nextExcluded });
							applyPpd(params.row, nextExcluded);
						}}
						label={params.row.excluded ? t("department.config.jobs.ppd.excluded") : t("department.config.jobs.ppd.included")}
						labelFontSize={FONT_SIZE.EMPHASIS}
						dataTestId={`dept-job-ppd-switch-${params.row.slug}`}
					/>
				),
		};

		const overrideColumn: SystemGridColDef<JobRow> = {
			field: "isOverridden",
			headerName: t("hris.mapping.column.isOverride"),
			width: OVERRIDE_COLUMN_WIDTH,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Box sx={{ width: "100%" }} data-testid={`dept-job-ppd-override-cell-${params.row.slug}`}>
					<HoverActionCell
						show={params.row.isOverridden}
						actionIcon={<RestoreIcon fontSize="small" />}
						tooltip={t("hris.mapping.resetToDefault")}
						onAction={() => {
							track("dept_job_ppd_override_reset", { department_id: departmentId, job_id: params.row.id });
							resetPpd(params.row);
						}}
					/>
				</Box>
			),
		};

		/**
		 * Role mode keeps its shift times on the ROLE, so a job row genuinely has no answer in either
		 * shift column. Tooltip rather than `aria-label` alone: the label hung off a <Typography> (a <p>),
		 * which left sighted users with an unexplained dash and is announced inconsistently by AT.
		 */
		const roleModeDash = (
			<InHouseTooltip title={t("department.config.jobs.shiftTimes.roleMode")}>
				<Typography sx={{ color: INK.DISABLED }} tabIndex={0} aria-label={t("department.config.jobs.shiftTimes.roleMode")}>
					—
				</Typography>
			</InHouseTooltip>
		);

		const partialShiftsColumn: SystemGridColDef<JobRow> = {
			field: "partialBlockShifts",
			headerName: t("department.config.shifts.col.partialShiftLengths"),
			flex: 1.1,
			minWidth: 150,
			renderCell: (params) =>
				jobMode ? (
					<ChipListCell
						items={params.row.partialBlockShifts.map((hours) => {
							const label = t("department.config.shifts.hoursShort", { hours });
							return { key: String(hours), label, tooltip: label };
						})}
						emptyLabel={t("department.config.jobs.shiftTimes.none")}
						maxVisible={MAX_VISIBLE_PARTIAL_CHIPS}
						expandable
						isOpen={openEditorRowIds.has(params.row.id)}
						dataTestId={`dept-job-partial-lengths-${params.row.slug}`}
					/>
				) : (
					roleModeDash
				),
		};

		const shiftTimesColumn: SystemGridColDef<JobRow> = {
			field: "fullShiftTimes",
			headerName: t("department.config.shifts.col.fullShiftTimes"),
			flex: 1.6,
			minWidth: 200,
			// Last labelled column before the unlabelled action gutter — a divider there reads as a
			// column boundary for a column that has no header.
			hideHeaderSeparator: true,
			renderCell: (params) =>
				jobMode ? (
					// `TagChip`'s tabular variant, so a column of time ranges aligns digit-for-digit and
					// matches every other read-only micro-tag in the app.
					<ChipListCell
						mono
						items={params.row.fullShiftTimes.map((range) => ({
							key: `${range[0]}-${range[1]}`,
							label: formatHourRange(range),
							tooltip: formatHourRange(range),
						}))}
						emptyLabel={t("department.config.jobs.shiftTimes.none")}
						maxVisible={MAX_VISIBLE_SHIFT_TIME_CHIPS}
						expandable
						isOpen={openEditorRowIds.has(params.row.id)}
						dataTestId={`dept-job-shift-times-${params.row.slug}`}
					/>
				) : (
					roleModeDash
				),
		};

		const actionsColumn: SystemGridColDef<JobRow> = {
			field: "id",
			headerName: "",
			width: ACTIONS_COLUMN_WIDTH,
			align: "center",
			headerAlign: "center",
			hideHeaderSeparator: true,
			excludeFromColumnPicker: true,
			renderCell: (params) =>
				params.row.isOrphan ? null : (
					<InHouseTooltip title={t("department.config.jobs.remove", { job: params.row.displayName })}>
						<IconButton
							size="small"
							onClick={() => removeJob(params.row)}
							aria-label={t("department.config.jobs.remove", { job: params.row.displayName })}
							data-testid={`dept-job-remove-${params.row.slug}`}
						>
							<DeleteOutlineOutlined fontSize="small" />
						</IconButton>
					</InHouseTooltip>
				),
		};

		// A new department has no id to hang a PPD override on, so those two columns only exist once saved.
		return isNewDept
			? [jobColumn, partialShiftsColumn, shiftTimesColumn, actionsColumn]
			: [jobColumn, ppdColumn, overrideColumn, partialShiftsColumn, shiftTimesColumn, actionsColumn];
		// Every dep here is stable across a keystroke or a settling mutation by construction; only a real
		// change of state rebuilds the columns.
	}, [t, isNewDept, jobMode, applyPpd, resetPpd, removeJob, track, departmentId, openEditorRowIds]);

	// ── Toolbar filters ───────────────────────────────────────────────────────────────────────────
	const groupFilterItems = useMemo(
		() => [
			{ value: ANY_FILTER, label: t("department.config.jobs.filter.allGroups") },
			...presentGroups.map((group) => ({ value: group.key, label: group.label })),
		],
		[presentGroups, t],
	);

	const ppdFilterItems = useMemo(
		() => [
			{ value: ANY_FILTER, label: t("department.config.jobs.filter.anyPpd") },
			{ value: PPD_INCLUDED, label: t("department.config.jobs.ppd.included") },
			{ value: PPD_EXCLUDED, label: t("department.config.jobs.ppd.excluded") },
		],
		[t],
	);

	const filterNodes = (
		<>
			<SystemDropdown
				id={groupFilterId}
				items={groupFilterItems}
				selectedItem={effectiveGroupFilter}
				onChange={(value) => setGroupFilter(value ?? ANY_FILTER)}
				// The "no filter" option's value is `""`, which the dropdown reads as an empty selection —
				// so the same label has to be the placeholder, or the resting control renders blank.
				placeholder={t("department.config.jobs.filter.allGroups")}
				formControlSx={{ minWidth: 170 }}
				data-testid="dept-jobs-group-filter"
			/>
			{!isNewDept && (
				<SystemDropdown
					id={ppdFilterId}
					items={ppdFilterItems}
					selectedItem={ppdFilter}
					onChange={(value) => setPpdFilter(value ?? ANY_FILTER)}
					placeholder={t("department.config.jobs.filter.anyPpd")}
					formControlSx={{ minWidth: 180 }}
					data-testid="dept-jobs-ppd-filter"
				/>
			)}
		</>
	);

	/**
	 * The same action from two places — the toolbar, and the zero-jobs empty state, whose `action` slot
	 * `EmptyState` renders unconditionally. A factory rather than one shared node because a test id
	 * identifies ONE element: sharing it put two `dept-config-manage-jobs-btn` nodes in the DOM for any
	 * department with no jobs, which is a Playwright strict-mode failure waiting for the first such test.
	 */
	const renderManageJobsButton = (dataTestId: string) => (
		<SystemButton
			buttonVariant="primary"
			size="small"
			leadingIcon={<AddCircleOutlined fontSize="small" />}
			onClick={() => setManageOpen(true)}
			data-testid={dataTestId}
		>
			{t("department.config.jobs.manage")}
		</SystemButton>
	);

	const clearFilters = useCallback(() => {
		setSearch("");
		setGroupFilter(ANY_FILTER);
		setPpdFilter(ANY_FILTER);
	}, []);

	/**
	 * Two different nothings, two different answers: the department genuinely has no jobs (offer the
	 * one action that fixes it), or it has jobs and the filters hid all of them (offer to clear them).
	 * Diagnosing the second as the first sent the operator to "Manage jobs" to re-add jobs they
	 * already had.
	 */
	const emptyStateNode =
		rows.length === 0 ? (
			<EmptyState
				icon={<WorkOutlineOutlined />}
				title={t("department.config.jobs.empty.title")}
				hint={t("department.config.jobs.empty.hint")}
				action={renderManageJobsButton("dept-config-jobs-empty-manage-jobs-btn")}
				dataTestId="dept-config-jobs-empty"
			/>
		) : (
			<EmptyState
				icon={<FilterAltOffOutlined />}
				title={t("department.config.jobs.empty.filtered.title")}
				hint={t("department.config.jobs.empty.filtered.hint")}
				action={
					<SystemButton buttonVariant="secondary" size="small" onClick={clearFilters} data-testid="dept-config-jobs-clear-filters">
						{t("department.config.jobs.empty.filtered.action")}
					</SystemButton>
				}
				dataTestId="dept-config-jobs-empty-filtered"
			/>
		);

	/**
	 * Both `pageSize: -1` ("All") AND `hideFooter` are required: SystemDataGrid's base defaults to
	 * pageSize 50 + a footer, so `hideFooter` alone strands rows past 50 with no way to reach page 2 —
	 * and grouping makes that worse, since each band row consumes a page slot too. Precedent:
	 * `NursesMatrixGrid`, `MinimizedNurseDataTable`.
	 */
	const jobsGridProps = useMemo(
		() => ({
			hideFooter: true,
			pageSizeOptions: [{ value: -1, label: t("All") }],
			initialState: { pagination: { paginationModel: { pageSize: -1 } } },
		}),
		[t],
	);

	/** The department's groups in canonical order, with the "not enabled" band pinned last. */
	const bandOrder = useMemo(() => [...groupOrder, ORPHAN_GROUP], [groupOrder]);
	const groupLabel = useCallback(
		(key: string) =>
			key === ORPHAN_GROUP ? t("department.config.jobs.group.notEnabled") : (groupByKey.get(key)?.label ?? t("department.config.jobs.ungrouped")),
		[groupByKey, t],
	);

	const rowGrouping = useMemo<RowGroupingOptions<JobRow>>(
		() => ({
			getGroupKey: (row) => row.groupKey,
			groupOrder: bandOrder,
			renderGroupLabel: (group) => (
				<>
					{/* The orphan band is not a department group, so it gets a neutral dot rather than
					    borrowing a group's legend colour. */}
					<ColorDot color={group.key === ORPHAN_GROUP ? NEUTRAL[400] : groupByKey.get(group.key)?.color} />
					<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: group.key === ORPHAN_GROUP ? INK.MUTED : INK.PRIMARY }}>
						{groupLabel(group.key)}
					</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
						{t("department.config.jobs.group.count", { count: group.rows.length })}
					</Typography>
				</>
			),
			getGroupAriaLabel: (group) => groupLabel(group.key),
		}),
		[bandOrder, groupByKey, groupLabel, t],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
			{/* The section's primary action sits over the grid, opposite the section title in the rail. */}
			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>{renderManageJobsButton("dept-config-manage-jobs-btn")}</Box>

			{/*
			 * Between "Manage jobs" and the list of enabled jobs, because that is the order the reader
			 * needs it in: the affected people, the job each of them is waiting on, and the one control
			 * that adds it — all without leaving the section. Read-only by design (DAT-834): a
			 * configuration mismatch is not a decision about a person.
			 */}
			{affectedJobItems.length > 0 && (
				<SystemBanner
					tone="warning"
					icon={<WarningAmberOutlined />}
					title={t("department.config.jobs.notEnabled.title", { count: jobNotEnabledEmployees.length })}
					// The checklist is block-level markup (a <ul>), which a <p> cannot legally contain.
					descriptionComponent="div"
					description={
						// Three beats, in the order the reader needs them: what this costs them, who it is
						// happening to, what to do about it. They were one paragraph, which buried the remedy
						// at the end of a third line of tinted 13px text.
						//
						// Capped at the annotation measure: the panel is as wide as a six-column grid, and left
						// uncapped the sentence ran ~1200px while each job's count chip drifted a screen away
						// from the name it counts.
						<Box sx={{ maxWidth: CONTENT_WIDTH.ANNOTATION }}>
							{t("department.config.jobs.notEnabled.body")}
							<SystemStatusChecklist
								items={affectedJobItems}
								ariaLabel={t("department.config.jobs.notEnabled.listLabel")}
								sx={{ my: 1.5 }}
								dataTestId="dept-config-jobs-not-enabled-list"
							/>
							{/* The one line the reader acts on — weighted so the eye lands on it after the list.
							    (Weight, not opacity: the description block already carries its own, and opacity
							    composites down rather than resetting.) */}
							<Box component="span" sx={{ display: "block", fontWeight: 600 }}>
								{t("department.config.jobs.notEnabled.fix")}
							</Box>
						</Box>
					}
					dataTestId="dept-config-jobs-not-enabled-banner"
				/>
			)}

			<SystemDataGrid<JobRow>
				rows={filteredRows}
				columns={columns}
				getRowId={(row) => row.id}
				loading={isLoadingJobs || isLoadingGroups}
				searchableColumns={[
					"displayName",
					// Group names are searchable without a column of their own — "Search jobs or groups".
					{ field: "groupLabel", getValue: (row) => row.groupLabel },
				]}
				searchPlaceholder={t("department.config.jobs.search.placeholder")}
				searchValue={search}
				onSearchChange={setSearch}
				trailingToolbarNodes={filterNodes}
				rowGrouping={rowGrouping}
				containedHeight={{ minHeight: 220, maxHeight: 560 }}
				dataGridProps={jobsGridProps}
				emptyState={emptyStateNode}
				// Click either shift column to edit that job's times in a panel below the row.
				getCellExpandContent={getCellExpandContent}
				getCellExpandContentHeight={getCellExpandContentHeight}
				expandedContentSx={expandedContentSx}
				onExpandedRowsChange={handleExpandedRowsChange}
				dataTestId="dept-config-jobs-grid"
			/>

			{jobMode && <ShiftTimesFallbackCard formData={formData} setFormData={setFormData} hourOptions={hourOptions} />}

			<ManageJobsDialog
				open={manageOpen}
				onClose={() => setManageOpen(false)}
				groups={groups}
				selectedJobIds={formData.jobIds}
				onConfirm={(jobIds) => setFormData((prev) => ({ ...prev, jobIds, jobPpdOverrides: prunePendingJobPpd(prev.jobPpdOverrides, jobIds) }))}
				departmentId={isNewDept ? undefined : departmentId}
				existingJobNames={allJobs.map((job) => job.name)}
				onJobCreated={handleJobCreated}
				source="shifts"
			/>
		</Box>
	);
};
